import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Shield, Plus, Edit, Trash2, Search, Users, FileText,
  CheckCircle, XCircle, Clock, Loader2, ArrowRight, History
} from 'lucide-react';
import { toast } from 'sonner';
import { RecalculateBalancesDialog } from '../components/RecalculateBalances';
import { BalanceAuditDialog } from '../components/BalanceAudit';
import { UsedDaysDialog } from '../components/UsedDaysDialog';
import { PolicyQuotaTable } from '../components/PolicyQuotaTable';
import { CompOffRegister } from '../components/CompOffRegister';

import { API_BASE as API_URL } from '../lib/apiBase';
const getHeaders = () => {
  const token = localStorage.getItem('hrms-token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
};

const LEAVE_TYPES = [
  'Casual Leave', 'Earned Leave', 'Medical Leave', 'Maternity Leave',
  'Paternity Leave', 'Study Leave', 'Academic Leave', 'On-Duty', 'Vacation Leave',
  'Half Day', 'Compensatory Off'
];

const APPOINTMENT_TYPES = ['Permanent', '11-Month Contract', 'Temporary', 'Contractual', 'Visiting', 'Ad-hoc', 'Probation'];

// ========== POLICY TEMPLATES TAB ==========
function PolicyTemplatesTab({ selectedEntity }) {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPolicy, setEditPolicy] = useState(null);
  const [form, setForm] = useState({
    name: '', appointment_type: 'Permanent',
    casual_leave: 12, earned_leave: 15, medical_leave: 10,
    maternity_leave: 180, paternity_leave: 15, study_leave: 10,
    academic_leave: 12, on_duty: 12, vacation_leave: 12,
    half_day: 12, compensatory_off: 8,
    disabled_categories: []
  });

  const fetchPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedEntity ? `?entity_id=${selectedEntity.entity_id}` : '';
      const res = await fetch(`${API_URL}/api/leave-policies${params}`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) setPolicies(await res.json());
    } catch { toast.error('Failed to load policies'); }
    finally { setLoading(false); }
  }, [selectedEntity]);

  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);

  const resetForm = () => {
    setForm({ name: '', appointment_type: 'Permanent', casual_leave: 12, earned_leave: 15, medical_leave: 10, maternity_leave: 180, paternity_leave: 15, study_leave: 10, academic_leave: 12, on_duty: 12, vacation_leave: 12, half_day: 12, compensatory_off: 8, disabled_categories: [] });
    setEditPolicy(null);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Policy name required'); return; }
    try {
      const body = {
        name: form.name,
        appointment_type: form.appointment_type,
        entity_id: selectedEntity?.entity_id || '',
        casual_leave: parseFloat(form.casual_leave) || 0,
        earned_leave: parseFloat(form.earned_leave) || 0,
        medical_leave: parseFloat(form.medical_leave) || 0,
        maternity_leave: parseFloat(form.maternity_leave) || 0,
        paternity_leave: parseFloat(form.paternity_leave) || 0,
        study_leave: parseFloat(form.study_leave) || 0,
        academic_leave: parseFloat(form.academic_leave) || 0,
        on_duty: parseFloat(form.on_duty) || 0,
        vacation_leave: parseFloat(form.vacation_leave) || 0,
        half_day: parseFloat(form.half_day) || 0,
        compensatory_off: parseFloat(form.compensatory_off) || 0,
        disabled_categories: form.disabled_categories || []
      };

      let res;
      if (editPolicy) {
        res = await fetch(`${API_URL}/api/leave-policies/${editPolicy.policy_id}`, {
          method: 'PUT', headers: getHeaders(), credentials: 'include',
          body: JSON.stringify(body)
        });
      } else {
        res = await fetch(`${API_URL}/api/leave-policies`, {
          method: 'POST', headers: getHeaders(), credentials: 'include',
          body: JSON.stringify(body)
        });
      }
      if (res.ok) {
        toast.success(editPolicy ? 'Policy updated' : 'Policy created');
        setDialogOpen(false); resetForm(); fetchPolicies();
      } else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed to save policy'); }
  };

  const handleDelete = async (policyId) => {
    if (!confirm('Delete this policy template?')) return;
    try {
      const res = await fetch(`${API_URL}/api/leave-policies/${policyId}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' });
      if (res.ok) { toast.success('Policy deleted'); fetchPolicies(); }
    } catch { toast.error('Failed to delete'); }
  };

  const handleToggleActive = async (policy) => {
    try {
      const res = await fetch(`${API_URL}/api/leave-policies/${policy.policy_id}`, {
        method: 'PUT', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify({ is_active: !policy.is_active })
      });
      if (res.ok) { toast.success(policy.is_active ? 'Policy disabled' : 'Policy enabled'); fetchPolicies(); }
      else { toast.error('Failed'); }
    } catch { toast.error('Failed'); }
  };

  const openEdit = (policy) => {
    setEditPolicy(policy);
    setForm({
      name: policy.name, appointment_type: policy.appointment_type,
      casual_leave: policy.leave_quotas?.['Casual Leave'] || 0,
      earned_leave: policy.leave_quotas?.['Earned Leave'] || 0,
      medical_leave: policy.leave_quotas?.['Medical Leave'] || 0,
      maternity_leave: policy.leave_quotas?.['Maternity Leave'] || 0,
      paternity_leave: policy.leave_quotas?.['Paternity Leave'] || 0,
      study_leave: policy.leave_quotas?.['Study Leave'] || 0,
      academic_leave: policy.leave_quotas?.['Academic Leave'] || 0,
      on_duty: policy.leave_quotas?.['On-Duty'] || 0,
      vacation_leave: policy.leave_quotas?.['Vacation Leave'] || 0,
      half_day: policy.leave_quotas?.['Half Day'] || 0,
      compensatory_off: policy.leave_quotas?.['Compensatory Off'] || 0,
      disabled_categories: policy.disabled_categories || []
    });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">Create policy templates based on appointment type and assign them to employees</p>
        <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="create-policy-btn"><Plus className="w-4 h-4 mr-1.5" />New Policy</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editPolicy ? 'Edit' : 'Create'} Leave Policy</DialogTitle>
              <DialogDescription>Define leave quotas for each appointment type</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Policy Name</Label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Permanent Faculty" data-testid="policy-name" />
                </div>
                <div className="space-y-1.5">
                  <Label>Appointment Type</Label>
                  <Select value={form.appointment_type} onValueChange={v => setForm({...form, appointment_type: v})}>
                    <SelectTrigger data-testid="policy-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{APPOINTMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="border-t pt-3">
                <Label className="text-sm font-semibold text-slate-700">Leave Categories & Quotas (days per year)</Label>
                <p className="text-xs text-slate-400 mb-3">Toggle to enable/disable each leave type. Set days to 0 if not applicable.</p>
                <div className="space-y-2">
                  {[
                    ['casual_leave', 'Casual Leave'], ['earned_leave', 'Earned Leave'], ['medical_leave', 'Medical Leave'],
                    ['maternity_leave', 'Maternity Leave'], ['paternity_leave', 'Paternity Leave'], ['study_leave', 'Study Leave'],
                    ['academic_leave', 'Academic Leave'], ['on_duty', 'On-Duty'], ['vacation_leave', 'Vacation Leave'],
                    ['half_day', 'Half Day'], ['compensatory_off', 'Compensatory Off']
                  ].map(([key, label]) => {
                    const isDisabled = (form.disabled_categories || []).includes(label);
                    const toggleCategory = () => {
                      const cats = form.disabled_categories || [];
                      setForm({...form, disabled_categories: isDisabled ? cats.filter(c => c !== label) : [...cats, label]});
                    };
                    return (
                      <div key={key} className={`flex items-center gap-3 p-2 rounded-lg border ${isDisabled ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-white border-slate-200'}`}>
                        <button type="button" onClick={toggleCategory} className={`w-10 h-5 rounded-full relative transition-colors ${isDisabled ? 'bg-slate-300' : 'bg-green-500'}`} data-testid={`toggle-${key}`}>
                          <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isDisabled ? 'left-0.5' : 'left-[22px]'}`} />
                        </button>
                        <Label className={`text-sm flex-1 ${isDisabled ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{label}</Label>
                        <Input type="number" min="0" step="0.5" value={form[key]} onChange={e => setForm({...form, [key]: e.target.value})}
                          disabled={isDisabled} className={`w-20 text-center ${isDisabled ? 'opacity-50' : ''}`} data-testid={`policy-${key}`} />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
                <Button className="btn-primary" onClick={handleSave} data-testid="save-policy-btn">
                  <CheckCircle className="w-4 h-4 mr-1.5" />{editPolicy ? 'Update' : 'Create'} Policy
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded animate-pulse" />)}</div>
      ) : policies.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><Shield className="w-12 h-12 mx-auto text-slate-300 mb-3" /><h3 className="font-medium">No policies yet</h3><p className="text-sm text-slate-500">Create your first leave policy template</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {policies.map(p => (
            <Card key={p.policy_id} data-testid={`policy-card-${p.policy_id}`} className={`hover:shadow-md transition-shadow ${!p.is_active ? 'opacity-60 border-dashed' : ''}`}>
              <CardHeader className="flex-row items-start justify-between pb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base">{p.name}</CardTitle>
                    <Badge className={p.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                      {p.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <Badge className="mt-1 bg-blue-100 text-blue-800">{p.appointment_type}</Badge>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => handleToggleActive(p)} 
                    className={p.is_active ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}
                    data-testid={`toggle-policy-${p.policy_id}`} title={p.is_active ? 'Disable' : 'Enable'}>
                    {p.is_active ? <XCircle className="w-3.5 h-3.5" /> : <CheckCircle className="w-3.5 h-3.5" />}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-policy-${p.policy_id}`}><Edit className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(p.policy_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(p.leave_quotas || {}).map(([type, days]) => {
                    const isDisabled = (p.disabled_categories || []).includes(type);
                    return (
                      <div key={type} className={`text-center p-1.5 rounded text-xs ${isDisabled ? 'bg-red-50 line-through opacity-50' : 'bg-slate-50'}`}>
                        <p className="text-slate-500 truncate">{type.replace(' Leave', '')}</p>
                        <p className={`font-bold ${isDisabled ? 'text-red-400' : 'text-slate-900'}`}>{days}</p>
                      </div>
                    );
                  })}
                </div>
                {p.disabled_categories?.length > 0 && (
                  <p className="text-[10px] text-red-500">Disabled: {p.disabled_categories.join(', ')}</p>
                )}
                <p className="text-xs text-slate-400">By {p.created_by_name} &bull; {new Date(p.created_at).toLocaleDateString()}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ========== EMPLOYEE LEAVE MANAGEMENT TAB ==========
function EmployeeLeaveTab({ selectedEntity }) {
  const [employees, setEmployees] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [empBalances, setEmpBalances] = useState([]);
  const [assignDialog, setAssignDialog] = useState(null);
  const [modifyDialog, setModifyDialog] = useState(null);
  const [selectedPolicyId, setSelectedPolicyId] = useState('');
  const [modifyForm, setModifyForm] = useState({ leave_type: '', new_total: 0, reason: '', comp_off_date: '' });
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [bulkAssignDialog, setBulkAssignDialog] = useState(false);
  const [bulkPolicyId, setBulkPolicyId] = useState('');
  const [bulkLeaveDialog, setBulkLeaveDialog] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [bulkLeaveForm, setBulkLeaveForm] = useState({ leave_type: '', total: '', reason: '' });
  const [assigningLeave, setAssigningLeave] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedEntity ? `?entity_id=${selectedEntity.entity_id}` : '';
      const [empRes, polRes, typRes] = await Promise.all([
        fetch(`${API_URL}/api/employees${params}`, { headers: getHeaders(), credentials: 'include' }),
        fetch(`${API_URL}/api/leave-policies${params}`, { headers: getHeaders(), credentials: 'include' }),
        fetch(`${API_URL}/api/leaves/types${params}`, { headers: getHeaders(), credentials: 'include' })
      ]);
      if (empRes.ok) setEmployees(await empRes.json());
      if (polRes.ok) setPolicies(await polRes.json());
      if (typRes.ok) {
        const t = await typRes.json();
        setLeaveTypes(Array.isArray(t) && t.length ? t.map(x => x.name) : LEAVE_TYPES);
      }
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [selectedEntity]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchBalance = async (empId) => {
    try {
      const res = await fetch(`${API_URL}/api/leaves/balance/${empId}`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) setEmpBalances(await res.json());
    } catch {}
  };

  const selectEmployee = (emp) => {
    setSelectedEmp(emp);
    fetchBalance(emp.employee_id);
  };

  const handleAssignPolicy = async () => {
    if (!assignDialog || !selectedPolicyId) { toast.error('Select a policy'); return; }
    try {
      const params = new URLSearchParams({ policy_id: selectedPolicyId });
      const res = await fetch(`${API_URL}/api/employees/${assignDialog}/apply-leave-policy?${params}`, {
        method: 'POST', headers: getHeaders(), credentials: 'include'
      });
      if (res.ok) {
        toast.success('Policy applied');
        setAssignDialog(null); setSelectedPolicyId('');
        if (selectedEmp?.employee_id === assignDialog) fetchBalance(assignDialog);
        fetchData();
      } else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed to apply policy'); }
  };

  const handleModifyLeave = async () => {
    if (!modifyDialog || !modifyForm.leave_type) return;
    try {
      const isComp = modifyForm.leave_type.toLowerCase().includes('compensatory');
      const params = new URLSearchParams({ leave_type: modifyForm.leave_type, reason: modifyForm.reason });
      if (isComp) {
        params.append('add_days', (parseFloat(modifyForm.add_days) || 0).toString());
        if (modifyForm.comp_off_date) params.append('comp_off_date', modifyForm.comp_off_date);
      } else {
        params.append('new_total', modifyForm.new_total.toString());
      }
      const res = await fetch(`${API_URL}/api/employees/${modifyDialog}/leave-balance?${params}`, {
        method: 'PUT', headers: getHeaders(), credentials: 'include'
      });
      if (res.ok) {
        toast.success('Leave balance updated');
        setModifyDialog(null); setModifyForm({ leave_type: '', new_total: 0, reason: '', comp_off_date: '' });
        if (selectedEmp?.employee_id === modifyDialog) fetchBalance(modifyDialog);
      } else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed to modify'); }
  };

  const handleBulkAssign = async () => {
    if (!bulkPolicyId || selectedEmployees.length === 0) { toast.error('Select a policy and employees'); return; }
    let success = 0;
    for (const empId of selectedEmployees) {
      try {
        const params = new URLSearchParams({ policy_id: bulkPolicyId });
        const res = await fetch(`${API_URL}/api/employees/${empId}/apply-leave-policy?${params}`, {
          method: 'POST', headers: getHeaders(), credentials: 'include'
        });
        if (res.ok) success++;
      } catch {}
    }
    toast.success(`Policy applied to ${success} of ${selectedEmployees.length} employees`);
    setBulkAssignDialog(false); setBulkPolicyId(''); setSelectedEmployees([]);
    fetchData();
  };

  const handleBulkLeaveAssign = async () => {
    if (!bulkLeaveForm.leave_type || bulkLeaveForm.total === '' || selectedEmployees.length === 0) {
      toast.error('Select leave category and enter leave count'); return;
    }
    setAssigningLeave(true);
    try {
      const res = await fetch(`${API_URL}/api/leaves/balance/bulk-assign`, {
        method: 'POST', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify({
          employee_ids: selectedEmployees,
          leave_type: bulkLeaveForm.leave_type,
          total: parseFloat(bulkLeaveForm.total),
          reason: bulkLeaveForm.reason
        })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed to assign');
      toast.success(d.message);
      setBulkLeaveDialog(false);
      setBulkLeaveForm({ leave_type: '', total: '', reason: '' });
      setSelectedEmployees([]);
      if (selectedEmp) fetchBalance(selectedEmp.employee_id);
    } catch (e) { toast.error(e.message); }
    finally { setAssigningLeave(false); }
  };

  const toggleEmpSelect = (empId) => {
    setSelectedEmployees(prev => prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]);
  };

  const toggleSelectAll = () => {
    if (selectedEmployees.length === filtered.length) setSelectedEmployees([]);
    else setSelectedEmployees(filtered.map(e => e.employee_id));
  };

  const filtered = employees.filter(e => {
    if (e.is_active === false) return false;
    const name = `${e.first_name} ${e.last_name}`.toLowerCase();
    return name.includes(searchTerm.toLowerCase()) || e.employee_id.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="space-y-5">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Search employees..." className="pl-10" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="emp-search" />
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-14 bg-slate-100 rounded animate-pulse" />)}</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Employee List */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Employees ({filtered.length})</CardTitle>
                {selectedEmployees.length > 0 && (
                  <div className="flex gap-1.5">
                    <Button size="sm" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white h-7 text-xs" onClick={() => setBulkAssignDialog(true)} data-testid="bulk-assign-btn">
                      <Shield className="w-3 h-3 mr-1" /> Policy ({selectedEmployees.length})
                    </Button>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs" onClick={() => { setBulkLeaveForm({ leave_type: '', total: '', reason: '' }); setBulkLeaveDialog(true); }} data-testid="bulk-leave-btn">
                      <Plus className="w-3 h-3 mr-1" /> Assign Leave ({selectedEmployees.length})
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0 max-h-[500px] overflow-y-auto">
              {/* Select All */}
              <div className="flex items-center gap-2 px-4 py-2 border-b bg-slate-50">
                <input type="checkbox" checked={selectedEmployees.length === filtered.length && filtered.length > 0} onChange={toggleSelectAll} className="rounded" />
                <span className="text-xs text-slate-500">Select All</span>
              </div>
              {filtered.map(emp => (
                <div
                  key={emp.employee_id}
                  className={`flex items-center gap-2 px-4 py-3 border-b cursor-pointer hover:bg-slate-50 transition-colors ${selectedEmp?.employee_id === emp.employee_id ? 'bg-red-50 border-l-2 border-l-[#DC2626]' : ''}`}
                  data-testid={`emp-row-${emp.employee_id}`}
                >
                  <input type="checkbox" checked={selectedEmployees.includes(emp.employee_id)} onChange={() => toggleEmpSelect(emp.employee_id)} onClick={e => e.stopPropagation()} className="rounded shrink-0" />
                  <div className="min-w-0 flex-1" onClick={() => selectEmployee(emp)}>
                    <p className="text-sm font-medium truncate">{emp.first_name} {emp.last_name}</p>
                    <p className="text-xs text-slate-400">{emp.designation || emp.appointment_type || 'N/A'} &bull; {emp.department}</p>
                    {emp.leave_policy_name && <Badge className="text-[10px] mt-0.5 bg-emerald-100 text-emerald-700">{emp.leave_policy_name}</Badge>}
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 shrink-0" onClick={() => selectEmployee(emp)} />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Employee Leave Details */}
          <Card className="lg:col-span-2">
            {selectedEmp ? (
              <>
                <CardHeader className="flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{selectedEmp.first_name} {selectedEmp.last_name}</CardTitle>
                    <p className="text-xs text-slate-500">{selectedEmp.designation} &bull; {selectedEmp.department} &bull; {selectedEmp.appointment_type || 'N/A'}</p>
                    {selectedEmp.leave_policy_name && <Badge className="mt-1 bg-emerald-100 text-emerald-700">{selectedEmp.leave_policy_name}</Badge>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setAssignDialog(selectedEmp.employee_id); setSelectedPolicyId(''); }} data-testid="assign-policy-btn">
                      <Shield className="w-3.5 h-3.5 mr-1" />Assign Policy
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setModifyDialog(selectedEmp.employee_id); setModifyForm({ leave_type: '', new_total: 0, reason: '', comp_off_date: '' }); }} data-testid="modify-leave-btn">
                      <Edit className="w-3.5 h-3.5 mr-1" />Modify Leave
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {empBalances.length === 0 ? (
                    <p className="text-center py-8 text-slate-500">No leave balances found. Assign a policy first.</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-3">
                      {empBalances.map(lb => {
                        const remaining = lb.total - lb.used;
                        const pct = lb.total > 0 ? (remaining / lb.total) * 100 : 0;
                        return (
                          <div key={lb.leave_type} className="p-3 rounded-lg border" data-testid={`emp-balance-${lb.leave_type}`}>
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{lb.leave_type.replace(/ Leave/g, '')}</p>
                            <div className="flex items-baseline gap-1 mt-1">
                              <span className="text-xl font-bold">{remaining}</span>
                              <span className="text-sm text-slate-400">/ {lb.total}</span>
                            </div>
                            <div className="w-full h-1.5 bg-slate-100 rounded-full mt-1.5 overflow-hidden">
                              <div className={`h-full rounded-full ${pct > 50 ? 'bg-emerald-500' : pct > 20 ? 'bg-amber-500' : 'bg-red-500'}`} style={{width: `${Math.max(pct, 0)}%`}} />
                            </div>
                            <p className="text-[10px] text-slate-400 mt-1">{lb.used} used</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </>
            ) : (
              <CardContent className="py-16 text-center">
                <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <h3 className="font-medium">Select an employee</h3>
                <p className="text-sm text-slate-500">Click on an employee to view and manage their leave balance</p>
              </CardContent>
            )}
          </Card>
        </div>
      )}

      {/* Assign Policy Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={() => setAssignDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Leave Policy</DialogTitle>
            <DialogDescription>Apply a policy template to update all leave quotas</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            {policies.length === 0 ? (
              <p className="text-sm text-amber-600">No policy templates created yet. Create one in the "Policy Templates" tab first.</p>
            ) : (
              <div className="space-y-2">
                <Label>Select Policy Template</Label>
                <Select value={selectedPolicyId} onValueChange={setSelectedPolicyId}>
                  <SelectTrigger data-testid="select-policy"><SelectValue placeholder="Choose a policy" /></SelectTrigger>
                  <SelectContent>
                    {policies.map(p => (
                      <SelectItem key={p.policy_id} value={p.policy_id}>
                        {p.name} ({p.appointment_type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedPolicyId && (() => {
                  const pol = policies.find(p => p.policy_id === selectedPolicyId);
                  if (!pol) return null;
                  return (
                    <div className="p-3 bg-slate-50 rounded-lg mt-2">
                      <p className="text-xs font-medium text-slate-500 mb-2">Leave quotas:</p>
                      <div className="grid grid-cols-3 gap-1.5">
                        {Object.entries(pol.leave_quotas || {}).map(([t, d]) => (
                          <div key={t} className="text-xs"><span className="text-slate-500">{t.replace(' Leave','')}: </span><span className="font-bold">{d}</span></div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setAssignDialog(null)}>Cancel</Button>
              <Button className="btn-primary" onClick={handleAssignPolicy} disabled={!selectedPolicyId} data-testid="confirm-assign-btn">
                <CheckCircle className="w-4 h-4 mr-1.5" />Apply Policy
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modify Leave Dialog */}
      <Dialog open={!!modifyDialog} onOpenChange={() => setModifyDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modify Individual Leave</DialogTitle>
            <DialogDescription>Override leave quota for a specific leave type</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div className="space-y-2">
              <Label>Leave Type</Label>
              <Select value={modifyForm.leave_type} onValueChange={v => {
                const existing = empBalances.find(b => b.leave_type === v);
                setModifyForm({...modifyForm, leave_type: v, new_total: existing?.total || 0 });
              }}>
                <SelectTrigger data-testid="modify-leave-type"><SelectValue placeholder="Select leave type" /></SelectTrigger>
                <SelectContent>{LEAVE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {modifyForm.leave_type?.toLowerCase().includes('compensatory') ? (
              <div className="space-y-2">
                <Label>Days to ADD <span className="text-xs text-slate-400">(added to current {empBalances.find(b => b.leave_type === modifyForm.leave_type)?.total || 0} — existing balance is kept)</span></Label>
                <Input type="number" min="0" step="0.5" value={modifyForm.add_days || ''} onChange={e => setModifyForm({...modifyForm, add_days: e.target.value})} placeholder="e.g. 1 or 0.5" data-testid="modify-add-days" />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>New Total (days/year)</Label>
                <Input type="number" min="0" step="0.5" value={modifyForm.new_total} onChange={e => setModifyForm({...modifyForm, new_total: e.target.value})} data-testid="modify-total" />
              </div>
            )}
            {modifyForm.leave_type?.toLowerCase().includes('compensatory') && (
              <div className="space-y-2">
                <Label>Worked on holiday date <span className="text-xs text-slate-400">(comp-off valid 1 month from this date)</span></Label>
                <Input type="date" value={modifyForm.comp_off_date} onChange={e => setModifyForm({...modifyForm, comp_off_date: e.target.value})} data-testid="comp-off-date-input" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Reason for Change</Label>
              <Input value={modifyForm.reason} onChange={e => setModifyForm({...modifyForm, reason: e.target.value})} placeholder="e.g., Contract extension approved" data-testid="modify-reason" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setModifyDialog(null)}>Cancel</Button>
              <Button className="btn-primary" onClick={handleModifyLeave} disabled={!modifyForm.leave_type} data-testid="confirm-modify-btn">
                <CheckCircle className="w-4 h-4 mr-1.5" />Update Balance
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Policy Dialog */}
      <Dialog open={bulkAssignDialog} onOpenChange={setBulkAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Assign Leave Policy</DialogTitle>
            <DialogDescription>Apply a leave policy to {selectedEmployees.length} selected employee(s)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1">
              {selectedEmployees.map(id => {
                const emp = employees.find(e => e.employee_id === id);
                return emp ? (
                  <div key={id} className="flex items-center justify-between text-xs bg-slate-50 p-1.5 rounded">
                    <span className="font-medium">{emp.first_name} {emp.last_name}</span>
                    <span className="text-slate-400">{emp.leave_policy_name || 'No policy'}</span>
                  </div>
                ) : null;
              })}
            </div>
            <div className="space-y-2">
              <Label>Select Policy</Label>
              <Select value={bulkPolicyId} onValueChange={setBulkPolicyId}>
                <SelectTrigger data-testid="bulk-select-policy"><SelectValue placeholder="Choose a policy" /></SelectTrigger>
                <SelectContent>
                  {policies.filter(p => p.is_active !== false).map(p => (
                    <SelectItem key={p.policy_id} value={p.policy_id}>{p.name} ({p.appointment_type})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setBulkAssignDialog(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={handleBulkAssign} disabled={!bulkPolicyId} data-testid="confirm-bulk-assign-btn">
                <CheckCircle className="w-4 h-4 mr-1.5" />Apply to {selectedEmployees.length} Employees
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Bulk Assign Leave Count Dialog */}
      <Dialog open={bulkLeaveDialog} onOpenChange={setBulkLeaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Leave to {selectedEmployees.length} Employee(s)</DialogTitle>
            <DialogDescription>Pick a leave category, enter the count and assign — it updates each employee&apos;s profile balance immediately.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1">
              {selectedEmployees.map(id => {
                const emp = employees.find(e => e.employee_id === id);
                return emp ? (
                  <div key={id} className="flex items-center justify-between text-xs bg-slate-50 p-1.5 rounded">
                    <span className="font-medium">{emp.first_name} {emp.last_name}</span>
                    <span className="text-slate-400">{emp.department || ''}</span>
                  </div>
                ) : null;
              })}
            </div>
            <div className="space-y-2">
              <Label>Leave Category</Label>
              <Select value={bulkLeaveForm.leave_type} onValueChange={v => setBulkLeaveForm({ ...bulkLeaveForm, leave_type: v })}>
                <SelectTrigger data-testid="bulk-leave-type"><SelectValue placeholder="Select leave category" /></SelectTrigger>
                <SelectContent>
                  {(leaveTypes.length ? leaveTypes : LEAVE_TYPES).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Leave Count (days/year)</Label>
              <Input type="number" min="0" step="0.5" value={bulkLeaveForm.total}
                onChange={e => setBulkLeaveForm({ ...bulkLeaveForm, total: e.target.value })}
                placeholder="e.g., 12" data-testid="bulk-leave-total" />
            </div>
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Input value={bulkLeaveForm.reason} onChange={e => setBulkLeaveForm({ ...bulkLeaveForm, reason: e.target.value })} placeholder="e.g., Annual quota 2026-27" data-testid="bulk-leave-reason" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setBulkLeaveDialog(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={handleBulkLeaveAssign} disabled={assigningLeave || !bulkLeaveForm.leave_type || bulkLeaveForm.total === ''} data-testid="confirm-bulk-leave-btn">
                {assigningLeave ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1.5" />}Assign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
// ========== ALL BALANCES TAB (comprehensive view) ==========
function AllBalancesTab({ selectedEntity }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [desigFilter, setDesigFilter] = useState('all');
  const [checked, setChecked] = useState({});
  const [delDialog, setDelDialog] = useState(false);
  const [delType, setDelType] = useState('__all__');
  const [delAll, setDelAll] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editBalances, setEditBalances] = useState([]);
  const [editReason, setEditReason] = useState('');
  const [newTypeName, setNewTypeName] = useState('');
  const [savingBal, setSavingBal] = useState(false);
  const [usedRow, setUsedRow] = useState(null);      // row whose USED days are being corrected
  const [usedType, setUsedType] = useState('');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedEntity ? `?entity_id=${selectedEntity.entity_id}` : '';
      const res = await fetch(`${API_URL}/api/leaves/balances/all${params}`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) { setRows(await res.json()); setChecked({}); }
    } catch { toast.error('Failed to load balances'); }
    finally { setLoading(false); }
  }, [selectedEntity]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const departments = [...new Set(rows.map(r => r.department).filter(Boolean))].sort();
  const designations = [...new Set(rows.map(r => r.designation).filter(Boolean))].sort();
  const allTypes = [...new Set(rows.flatMap(r => Object.keys(r.balances || {})))].sort();

  const filtered = rows.filter(r => {
    if (deptFilter !== 'all' && r.department !== deptFilter) return false;
    if (desigFilter !== 'all' && r.designation !== desigFilter) return false;
    const s = search.toLowerCase();
    return !s || r.employee_name.toLowerCase().includes(s) || r.employee_id.toLowerCase().includes(s);
  });
  const selIds = filtered.filter(r => checked[r.employee_id]).map(r => r.employee_id);

  const handleBulkDelete = async () => {
    try {
      const payload = delAll
        ? { all: true, entity_id: selectedEntity?.entity_id || null, leave_type: delType === '__all__' ? null : delType }
        : { employee_ids: selIds, leave_type: delType === '__all__' ? null : delType };
      const res = await fetch(`${API_URL}/api/leaves/balances/bulk-delete`, {
        method: 'POST', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify(payload)
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed to delete');
      toast.success(d.message);
      setDelDialog(false); setDelType('__all__'); setDelAll(false);
      fetchRows();
    } catch (e) { toast.error(e.message); }
  };

  const openEdit = (r) => {
    const existing = r.balances || {};
    const base = ['Casual Leave', 'Medical Leave', 'On-Duty', 'Compensatory Off'];
    const types = [...new Set([...Object.keys(existing), ...base])];
    const list = types.map(t => ({
      leave_type: t,
      total: existing[t] ? String(existing[t].total) : '0',
      used: existing[t] ? existing[t].used : 0,
      employee_visible: existing[t] ? existing[t].employee_visible !== false : true,
      is_comp: t.toLowerCase().includes('compensatory'),
    }));
    setEditRow(r); setEditBalances(list); setEditReason(''); setNewTypeName('');
  };

  const addCustomType = () => {
    const name = newTypeName.trim();
    if (!name) return;
    if (editBalances.some(b => b.leave_type.toLowerCase() === name.toLowerCase())) {
      toast.error('That leave type already exists'); return;
    }
    setEditBalances([...editBalances, { leave_type: name, total: '0', used: 0, employee_visible: true, is_comp: name.toLowerCase().includes('compensatory') }]);
    setNewTypeName('');
  };

  const handleBulkBalanceSave = async () => {
    setSavingBal(true);
    try {
      const payload = {
        reason: editReason,
        balances: editBalances.map(b => ({ leave_type: b.leave_type, total: parseFloat(b.total || 0) || 0, employee_visible: !!b.employee_visible })),
      };
      const res = await fetch(`${API_URL}/api/employees/${editRow.employee_id}/leave-balances`, {
        method: 'PUT', headers: getHeaders(), credentials: 'include', body: JSON.stringify(payload),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed to save');
      toast.success(d.message);
      setEditRow(null); fetchRows();
    } catch (e) { toast.error(e.message); }
    finally { setSavingBal(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search employee..." className="pl-10" value={search} onChange={e => setSearch(e.target.value)} data-testid="bal-search" />
        </div>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-full md:w-48" data-testid="bal-dept-filter"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={desigFilter} onValueChange={setDesigFilter}>
          <SelectTrigger className="w-full md:w-48" data-testid="bal-desig-filter"><SelectValue placeholder="Designation" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Designations</SelectItem>
            {designations.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        {selIds.length > 0 && (
          <Button variant="outline" className="border-red-500 text-red-600 hover:bg-red-50" onClick={() => { setDelAll(false); setDelDialog(true); }} data-testid="bal-bulk-delete-btn">
            <Trash2 className="w-4 h-4 mr-1.5" />Delete ({selIds.length})
          </Button>
        )}
        <RecalculateBalancesDialog selectedEntity={selectedEntity} onDone={fetchRows} />
        <BalanceAuditDialog selectedEntity={selectedEntity} onDone={fetchRows} />
        <Button variant="outline" className="border-red-600 text-red-700 hover:bg-red-50" onClick={() => { setDelAll(true); setDelDialog(true); }} data-testid="bal-delete-all-btn">
          <Trash2 className="w-4 h-4 mr-1.5" />Reset All
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />)}</div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      <input type="checkbox"
                        checked={filtered.length > 0 && selIds.length === filtered.length}
                        onChange={(e) => {
                          const m = { ...checked };
                          filtered.forEach(r => { m[r.employee_id] = e.target.checked; });
                          setChecked(m);
                        }}
                        data-testid="bal-select-all" />
                    </TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Designation</TableHead>
                    {allTypes.map(t => <TableHead key={t} className="text-center text-[11px] whitespace-nowrap">{t.replace(' Leave', '')}<p className="text-[9px] font-normal text-slate-400">bal / total</p></TableHead>)}
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(r => (
                    <TableRow key={r.employee_id} data-testid={`bal-row-${r.employee_id}`}>
                      <TableCell>
                        <input type="checkbox" checked={!!checked[r.employee_id]}
                          onChange={(e) => setChecked({ ...checked, [r.employee_id]: e.target.checked })}
                          data-testid={`bal-check-${r.employee_id}`} />
                      </TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">{r.employee_name}</p>
                        <code className="text-xs text-slate-400">{r.employee_id}</code>
                      </TableCell>
                      <TableCell className="text-xs">{r.department || '—'}</TableCell>
                      <TableCell className="text-xs">{r.designation || '—'}</TableCell>
                      {allTypes.map(t => {
                        const b = (r.balances || {})[t];
                        return (
                          <TableCell key={t} className="text-center text-xs">
                            {b ? <span><strong>{b.balance}</strong><span className="text-slate-400"> / {b.total}</span></span> : <span className="text-slate-300">—</span>}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs"
                          onClick={() => { setUsedType(''); setUsedRow(r); }}
                          title="Correct used days (allotted stays unchanged)"
                          data-testid={`bal-used-${r.employee_id}`}>
                          Used
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2"
                          onClick={() => openEdit(r)}
                          data-testid={`bal-edit-${r.employee_id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow><TableCell colSpan={5 + allTypes.length} className="text-center py-10 text-slate-500 text-sm">No leave records found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <UsedDaysDialog
        open={!!usedRow}
        employee={usedRow}
        leaveTypes={usedRow ? Object.keys(usedRow.balances || {}).sort() : []}
        defaultType={usedType}
        onClose={() => setUsedRow(null)}
        onDone={fetchRows}
      />

      {/* Bulk Delete Dialog */}
      <Dialog open={delDialog} onOpenChange={setDelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{delAll ? 'Reset ALL Leave Balances' : 'Delete Leave Records'}</DialogTitle>
            <DialogDescription>
              {delAll
                ? `This wipes leave balance records for EVERY employee in ${selectedEntity?.name || 'all institutes'}. This cannot be undone.`
                : `Delete leave balance records for ${selIds.length} selected employee(s). This cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div className="space-y-2">
              <Label>Leave Category</Label>
              <Select value={delType} onValueChange={setDelType}>
                <SelectTrigger data-testid="bal-del-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All categories</SelectItem>
                  {allTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setDelDialog(false)}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleBulkDelete} data-testid="bal-del-confirm">
                <Trash2 className="w-4 h-4 mr-1.5" />Delete Records
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Balance Dialog — all leave types for this employee */}
      <Dialog open={!!editRow} onOpenChange={() => setEditRow(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Leaves — {editRow?.employee_name}</DialogTitle>
            <DialogDescription>Set the allotted days for every leave type and choose which ones the employee can see when applying. Comp-Off days are credited from the Comp-Off register.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2 max-h-[55vh] overflow-y-auto pr-1" data-testid="bal-edit-list">
            <div className="grid grid-cols-[1fr_6rem_7rem] gap-3 px-3 text-[11px] font-medium text-slate-400 uppercase">
              <span>Leave Type</span><span className="text-center">Days/Yr</span><span className="text-right">Show to Staff</span>
            </div>
            {editBalances.map((b, i) => (
              <div key={b.leave_type} className="grid grid-cols-[1fr_6rem_7rem] gap-3 items-center border rounded-md px-3 py-2" data-testid={`bal-edit-row-${b.leave_type.replace(/\s+/g, '-')}`}>
                <div>
                  <p className="text-sm font-medium">{b.leave_type}</p>
                  <p className="text-[11px] text-slate-400">used {b.used}{b.is_comp ? ' · ledger-driven' : ''}</p>
                </div>
                <Input type="number" min="0" step="0.5" value={b.total} disabled={b.is_comp}
                  className="h-8 text-center"
                  onChange={e => setEditBalances(arr => arr.map((x, idx) => idx === i ? { ...x, total: e.target.value } : x))}
                  data-testid={`bal-edit-days-${b.leave_type.replace(/\s+/g, '-')}`} />
                <label className="flex items-center gap-1.5 text-xs justify-end cursor-pointer">
                  <Switch checked={b.employee_visible}
                    onCheckedChange={v => setEditBalances(arr => arr.map((x, idx) => idx === i ? { ...x, employee_visible: v } : x))}
                    data-testid={`bal-edit-visible-${b.leave_type.replace(/\s+/g, '-')}`} />
                  <span className={b.employee_visible ? 'text-emerald-600' : 'text-slate-400'}>{b.employee_visible ? 'Shown' : 'Hidden'}</span>
                </label>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-2 border-t">
            <Input placeholder="Add a custom leave type (e.g. Sabbatical)" value={newTypeName}
              onChange={e => setNewTypeName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomType(); } }}
              data-testid="bal-edit-new-type" />
            <Button type="button" variant="outline" onClick={addCustomType} data-testid="bal-edit-add-type">
              <Plus className="w-4 h-4 mr-1" />Add
            </Button>
          </div>
          <div className="space-y-2 pt-1">
            <Label>Reason (optional)</Label>
            <Input value={editReason} onChange={e => setEditReason(e.target.value)} data-testid="bal-edit-reason" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setEditRow(null)}>Cancel</Button>
            <Button className="btn-primary" onClick={handleBulkBalanceSave} disabled={savingBal} data-testid="bal-edit-save">
              <CheckCircle className="w-4 h-4 mr-1.5" />Save All
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CorrectUsedTab({ selectedEntity }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [target, setTarget] = useState(null); // { row, leave_type }

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedEntity ? `?entity_id=${selectedEntity.entity_id}` : '';
      const res = await fetch(`${API_URL}/api/leaves/balances/all${params}`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) setRows(await res.json());
    } catch { toast.error('Failed to load balances'); }
    finally { setLoading(false); }
  }, [selectedEntity]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const allTypes = [...new Set(rows.flatMap(r => Object.keys(r.balances || {})))].sort();

  // One line per staff member × leave policy
  const lines = [];
  rows.forEach(r => {
    Object.entries(r.balances || {}).forEach(([type, b]) => {
      if (typeFilter !== 'all' && type !== typeFilter) return;
      const s = search.trim().toLowerCase();
      if (s && !r.employee_name.toLowerCase().includes(s) && !r.employee_id.toLowerCase().includes(s)) return;
      lines.push({ row: r, type, ...b });
    });
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Correct Used Days</CardTitle>
          <CardDescription>
            Pick a leave policy and staff member. The allotted days stay locked — only the used days can be edited,
            and the balance recalculates automatically.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search staff name or ID..." className="pl-10" value={search}
                onChange={e => setSearch(e.target.value)} data-testid="used-search" />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="md:w-64" data-testid="used-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Leave Policies</SelectItem>
                {allTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Leave Policy</TableHead>
                    <TableHead>Staff Name</TableHead>
                    <TableHead className="text-center">Allotted</TableHead>
                    <TableHead className="text-center">Used</TableHead>
                    <TableHead className="text-center">Balance</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.slice(0, 400).map(l => (
                    <TableRow key={`${l.row.employee_id}-${l.type}`} data-testid={`used-row-${l.row.employee_id}-${l.type.replace(/\s+/g, '-')}`}>
                      <TableCell className="text-sm font-medium">{l.type}</TableCell>
                      <TableCell>
                        <p className="text-sm">{l.row.employee_name}</p>
                        <code className="text-xs text-slate-400">{l.row.employee_id}</code>
                      </TableCell>
                      <TableCell className="text-center text-sm">{l.total}</TableCell>
                      <TableCell className="text-center text-sm font-semibold">{l.used}</TableCell>
                      <TableCell className="text-center text-sm">{l.balance}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => setTarget({ row: l.row, leave_type: l.type })}
                          data-testid={`used-edit-${l.row.employee_id}-${l.type.replace(/\s+/g, '-')}`}>
                          <Edit className="w-3.5 h-3.5 mr-1" /> Edit Used
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {lines.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500 text-sm">No leave balances found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              {lines.length > 400 && (
                <p className="text-xs text-slate-500 p-3">Showing the first 400 rows — narrow the search to see more.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <UsedDaysDialog
        open={!!target}
        employee={target?.row}
        leaveTypes={target ? Object.keys(target.row.balances || {}).sort() : []}
        defaultType={target?.leave_type || ''}
        onClose={() => setTarget(null)}
        onDone={fetchRows}
      />
    </div>
  );
}

function ChangeLogTab({ selectedEntity, isSuperAdmin }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const params = selectedEntity && !isSuperAdmin ? `?entity_id=${selectedEntity.entity_id}` : '';
        const res = await fetch(`${API_URL}/api/leave-policy-logs${params}`, { headers: getHeaders(), credentials: 'include' });
        if (res.ok) setLogs(await res.json());
      } catch { toast.error('Failed to load logs'); }
      finally { setLoading(false); }
    };
    fetchLogs();
  }, [selectedEntity, isSuperAdmin]);

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">
        {isSuperAdmin ? 'All leave policy changes across all institutes' : 'Leave policy changes for your institute'}
      </p>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />)}</div>
      ) : logs.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><History className="w-12 h-12 mx-auto text-slate-300 mb-3" /><h3 className="font-medium">No changes yet</h3><p className="text-sm text-slate-500">Policy modifications will appear here</p></CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Changed By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map(log => (
                    <TableRow key={log.log_id} data-testid={`log-${log.log_id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{new Date(log.created_at).toLocaleDateString('en-IN')} {new Date(log.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">{log.employee_name}</p>
                        <code className="text-xs text-slate-400">{log.employee_id}</code>
                      </TableCell>
                      <TableCell>
                        <Badge className={log.action === 'policy_applied' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>
                          {log.action === 'policy_applied' ? 'Policy Applied' : 'Individual Change'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm max-w-[250px]">
                        <p className="truncate">{log.details}</p>
                        {log.reason && <p className="text-xs text-slate-400 truncate">Reason: {log.reason}</p>}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{log.changed_by_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ========== MAIN PAGE ==========
export default function LeavePolicy() {
  const { selectedEntity } = useOutletContext() || {};
  const { user } = useAuth();
  const userRole = user?.role || 'employee';
  const isSuperAdmin = userRole === 'super_admin';
  const canManage = ['hr', 'admin', 'super_admin', 'branch_admin', 'institute_admin'].includes(userRole);

  if (!canManage) {
    return (
      <div className="text-center py-20">
        <Shield className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold">Access Restricted</h2>
        <p className="text-slate-500">Only HR and Super Admin can manage leave policies</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in" data-testid="leave-policy-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Leave Policy Management</h1>
        <p className="text-sm text-slate-500 mt-1">
          {isSuperAdmin ? 'Manage and monitor leave policies across all institutes' : 'Configure leave policies for your institute employees'}
        </p>
      </div>

      <Tabs defaultValue="balances" className="w-full">
        <TabsList data-testid="leave-policy-tabs">
          {canManage && (
            <>
              <TabsTrigger value="balances" data-testid="tab-balances">
                <FileText className="w-3.5 h-3.5 mr-1.5" />All Balances
              </TabsTrigger>
              <TabsTrigger value="used-days" data-testid="tab-used-days">
                <Edit className="w-3.5 h-3.5 mr-1.5" />Correct Used Days
              </TabsTrigger>
              <TabsTrigger value="comp-off" data-testid="tab-comp-off-register">
                <Clock className="w-3.5 h-3.5 mr-1.5" />Comp-Off Register
              </TabsTrigger>
            </>
          )}
          <TabsTrigger value="logs" data-testid="tab-logs">
            <History className="w-3.5 h-3.5 mr-1.5" />Change Log
          </TabsTrigger>
        </TabsList>

        <TabsContent value="balances">
          <AllBalancesTab selectedEntity={selectedEntity} />
        </TabsContent>
        <TabsContent value="used-days">
          <CorrectUsedTab selectedEntity={selectedEntity} />
        </TabsContent>
        <TabsContent value="comp-off">
          <CompOffRegister selectedEntity={selectedEntity} canAdd={['hr', 'admin', 'super_admin'].includes(userRole)} />
        </TabsContent>
        <TabsContent value="logs">
          <ChangeLogTab selectedEntity={selectedEntity} isSuperAdmin={isSuperAdmin} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
